import { AlertTriangle, ArrowRightLeft, Bot, Sparkles, Workflow, Wrench } from 'lucide-react';
import { useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import { fmtInt, fmtMs, fmtUsd } from '@/lib/format';
import type { SpanRow, TraceDetail } from '@/lib/types';
import { JsonView, MessagesView, ResponseView } from './JsonView';
import { Badge, Stat } from './ui';

const ICON = { agent: Bot, llm: Sparkles, tool: Wrench, handoff: ArrowRightLeft, guardrail: AlertTriangle, custom: Sparkles, step: Workflow };
const BAR = { agent: 'bg-ink/70', llm: 'bg-brand', tool: 'bg-warn', handoff: 'bg-ok', guardrail: 'bg-bad', custom: 'bg-ink-3', step: 'bg-ink-3' };

type Node = SpanRow & { depth: number };

const flatten = (spans: SpanRow[]): Node[] => {
  const children = new Map<string | null, SpanRow[]>();
  const ids = new Set(spans.map((s) => s.id));
  for (const s of spans) {
    const parent = s.parentId && ids.has(s.parentId) ? s.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), s]);
  }
  const out: Node[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const s of (children.get(parent) ?? []).sort((a, b) => (a.startedAt ?? '').localeCompare(b.startedAt ?? ''))) {
      out.push({ ...s, depth });
      walk(s.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
};

/** Waterfall of spans + detail pane for the selected span (or the whole trace). */
export const TraceView = ({ detail }: { detail: TraceDetail }) => {
  const { trace, spans } = detail;
  const nodes = useMemo(() => flatten(spans), [spans]);
  const [selected, setSelected] = useState<string | null>(null);
  const span = nodes.find((n) => n.id === selected) ?? null;

  const t0 = Math.min(...spans.map((s) => (s.startedAt ? Date.parse(s.startedAt) : Infinity)), Date.parse(trace.startedAt));
  const total = Math.max(trace.durationMs, 1);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-4 rounded-lg border border-line bg-panel p-4 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Status" value={trace.status === 'ok' ? <Badge tone="ok">ok</Badge> : <Badge tone="bad">error</Badge>} />
        <Stat label="Duration" value={fmtMs(trace.durationMs)} />
        <Stat label="Input tokens" value={fmtInt(trace.inputTokens)} />
        <Stat label="Output tokens" value={fmtInt(trace.outputTokens)} />
        <Stat label="Cost" value={fmtUsd(trace.costUsd)} />
        <Stat label="Version" value={trace.version ? `v${trace.version}` : trace.source === 'playground' ? 'draft' : 'fallback'} />
      </div>
      {trace.error && <div className="rounded-md border border-bad/20 bg-bad-soft px-3 py-2 text-[13px] break-words text-bad">{trace.error}</div>}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="overflow-hidden rounded-lg border border-line bg-panel">
          <button
            onClick={() => setSelected(null)}
            className={cn('flex w-full items-center justify-between border-b border-line px-3 py-2 text-left text-[13px] font-medium', !selected && 'bg-brand-soft')}
          >
            <span>Whole request</span>
            <span className="text-ink-3 tabular-nums">{fmtMs(trace.durationMs)}</span>
          </button>
          {nodes.length === 0 && <p className="px-3 py-4 text-[13px] text-ink-3">No spans recorded (the run failed before the model was called).</p>}
          <ul>
            {nodes.map((n) => {
              const Icon = ICON[n.type] ?? Sparkles;
              const start = n.startedAt ? Date.parse(n.startedAt) - t0 : 0;
              const left = Math.min(100, (start / total) * 100);
              const width = Math.max(0.8, Math.min(100 - left, ((n.latencyMs ?? 0) / total) * 100));
              return (
                <li key={n.id}>
                  <button
                    onClick={() => setSelected(n.id)}
                    className={cn('grid w-full grid-cols-[minmax(0,1fr)_120px] items-center gap-3 px-3 py-1.5 text-left text-[13px] hover:bg-line-2', selected === n.id && 'bg-brand-soft')}
                  >
                    <span className="flex min-w-0 items-center gap-1.5" style={{ paddingLeft: n.depth * 14 }}>
                      <Icon className={cn('size-3.5 shrink-0', n.status === 'error' ? 'text-bad' : 'text-ink-2')} />
                      <span className="truncate">{n.name}</span>
                      {n.type === 'llm' && n.inputTokens !== null && <span className="shrink-0 text-[11px] text-ink-3 tabular-nums">{fmtInt((n.inputTokens ?? 0) + (n.outputTokens ?? 0))} tok</span>}
                    </span>
                    <span className="relative h-2 rounded-full bg-line-2" title={fmtMs(n.latencyMs)}>
                      <span className={cn('absolute top-0 h-2 rounded-full', BAR[n.type] ?? 'bg-ink-3')} style={{ left: `${left}%`, width: `${width}%` }} />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="min-w-0 rounded-lg border border-line bg-panel p-4">
          {span ? <SpanDetail span={span} /> : <RequestDetail detail={detail} />}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="mb-4">
    <h4 className="mb-1.5 text-[11px] font-semibold tracking-wide text-ink-3 uppercase">{title}</h4>
    {children}
  </div>
);

const RequestDetail = ({ detail }: { detail: TraceDetail }) => {
  const input = detail.trace.input as Record<string, unknown> | null;
  return (
    <>
      <Section title="Subject">
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(detail.trace.subject ?? {}).map(([k, v]) => (
            <Badge key={k}>
              {k}: <span className="mono">{String(v)}</span>
            </Badge>
          ))}
          {!Object.keys(detail.trace.subject ?? {}).length && <span className="text-[13px] text-ink-3">—</span>}
        </div>
      </Section>
      {input && typeof input === 'object' && 'system' in input ? (
        <>
          <Section title="Rendered system prompt">
            <JsonView value={input.system} />
          </Section>
          <Section title="Variables & input">
            <JsonView value={{ variables: input.variables, input: input.input, messages: input.messages, files: input.files }} />
          </Section>
        </>
      ) : (
        <Section title="Input">
          <JsonView value={input} empty="Payload logging is off for this agent" />
        </Section>
      )}
      <Section title="Output">
        <JsonView value={detail.trace.output} empty="Payload logging is off for this agent" />
      </Section>
    </>
  );
};

const SpanDetail = ({ span }: { span: Node }) => (
  <>
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <Badge tone="brand">{span.type}</Badge>
      <span className="font-semibold">{span.name}</span>
      {span.status === 'error' && <Badge tone="bad">error</Badge>}
    </div>
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <Stat label="Latency" value={fmtMs(span.latencyMs)} />
      {span.type === 'llm' && (
        <>
          <Stat label="Tokens in / out" value={`${fmtInt(span.inputTokens)} / ${fmtInt(span.outputTokens)}`} />
          <Stat label="Cached" value={fmtInt(span.cachedTokens)} />
          <Stat label="Cost" value={fmtUsd(span.costUsd)} />
        </>
      )}
    </div>
    {span.error && <div className="mb-4 rounded-md border border-bad/20 bg-bad-soft px-3 py-2 text-[13px] break-words text-bad">{span.error}</div>}
    {span.type === 'llm' ? (
      <>
        <Section title="Messages sent">
          <MessagesView value={span.input} />
        </Section>
        <Section title="Response">
          <ResponseView value={span.output} />
        </Section>
        {span.attributes?.modelConfig ? (
          <Section title="Model settings">
            <JsonView value={span.attributes.modelConfig} />
          </Section>
        ) : null}
      </>
    ) : (
      <>
        {span.input !== null && span.input !== undefined && (
          <Section title="Input">
            <JsonView value={span.input} />
          </Section>
        )}
        {span.output !== null && span.output !== undefined && (
          <Section title="Output">
            <JsonView value={span.output} />
          </Section>
        )}
        {span.attributes && Object.keys(span.attributes).length > 0 && (
          <Section title="Details">
            <JsonView value={span.attributes} />
          </Section>
        )}
      </>
    )}
  </>
);
